import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getIdeaById } from '../services/api';
import { stringToColor, readableTextColor } from '../utils/color';
import { getFaviconUrl, getFaviconFallback } from '../utils/favicon';

function Section({ title, children }) {
  return (
    <section className="panel panel--muted" style={{ marginBottom: 12 }}>
      <h3 style={{ marginTop: 0 }}>{title}</h3>
      <div>{children}</div>
    </section>
  );
}

function CompetitorItem({ competitor }) {
  const [iconUrl, setIconUrl] = useState(null);
  useEffect(() => {
    let active = true;
    async function run() {
      try {
        const resolverUrl = getFaviconUrl(competitor?.url);
        if (!resolverUrl) {
          if (active) setIconUrl(getFaviconFallback());
          return;
        }
        const res = await fetch(resolverUrl, { credentials: 'omit' });
        if (!res.ok) throw new Error('no icon');
        const data = await res.json();
        const finalUrl = data?.url || null;
        if (active) setIconUrl(finalUrl || getFaviconFallback());
      } catch (e) {
        if (active) setIconUrl(getFaviconFallback());
      }
    }
    run();
    return () => { active = false; };
  }, [competitor && competitor.url]);

  let host = '';
  try { host = new URL(competitor.url).hostname; } catch (e) {}
  return (
    <a href={competitor.url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
      <img src={iconUrl || getFaviconFallback()} alt="favicon" width={16} height={16} style={{ borderRadius: 2 }} onError={(e) => { e.currentTarget.src = getFaviconFallback(); }} />
      <span>{competitor.name}</span>
      {host && <span className="text-muted" style={{ fontSize: 12 }}>({host})</span>}
    </a>
  );
}

export default function IdeaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getIdeaById(id);
        if (mounted) setIdea(data);
      } catch (e) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  return (
    <main className="container" style={{ paddingTop: 24, paddingBottom: 40 }}>
      <section className="panel panel--elevated" style={{ padding: 28 }}>
        <div style={{ marginBottom: 16 }}>
          <button className="btn" onClick={() => navigate(-1)}><i className="fa-solid fa-arrow-left" style={{ marginRight: 6 }} />Back</button>
        </div>

        {loading && (<p>Loading…</p>)}
        {error && (<p style={{ color: 'var(--color-danger)' }}>Failed to load idea.</p>)}

        {idea && (
          <>
            <header className="grid" style={{ gap: 8 }}>
              <h2 style={{ margin: 0 }}>{idea.title}</h2>
              {(idea.category?.name || idea.master_category) && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {idea.category?.name && (() => {
                    const bg = stringToColor(idea.category.name);
                    const color = readableTextColor(bg);
                    return <span className="chip" style={{ background: bg, color }}>{idea.category.name}</span>;
                  })()}
                  {idea.master_category && (() => {
                    const bg = stringToColor(idea.master_category);
                    const color = readableTextColor(bg);
                    return <span className="chip" style={{ background: bg, color }}>{idea.master_category}</span>;
                  })()}
                </div>
              )}
            </header>

            {idea.description && <p style={{ marginTop: 12, marginBottom: 12 }}>{idea.description}</p>}

            {Array.isArray(idea.features) && idea.features.length > 0 && (
              <>
                <hr className="divider" />
                <h4 style={{ marginTop: 0, marginBottom: 8 }}>Features</h4>
                <ul className="list-clean">
                  {idea.features.map((f, idx) => (
                    <li key={idx} className="list-item">
                      <span className="list-item-icon icon-primary"><i className="fa-solid fa-check" /></span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {Array.isArray(idea.pain_points) && idea.pain_points.length > 0 && (
              <>
                <hr className="divider" />
                <h4 style={{ marginTop: 0, marginBottom: 8 }}>Pain points</h4>
                <ul className="list-clean">
                  {idea.pain_points.map((p, idx) => (
                    <li key={idx} className="list-item">
                      <span className="list-item-icon icon-danger"><i className="fa-solid fa-xmark" /></span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <hr className="divider" />
            <h4 style={{ marginTop: 0, marginBottom: 8 }}>Competitors</h4>
            {Array.isArray(idea.competitors) && idea.competitors.length > 0 ? (
              <div className="grid" style={{ gap: 8 }}>
                {idea.competitors.map((c) => (
                  <CompetitorItem key={c.id || c.url} competitor={c} />
                ))}
              </div>
            ) : (
              <p className="text-muted" style={{ margin: 0 }}>No competitors listed.</p>
            )}
          </>
        )}
      </section>
    </main>
  );
}


